import {
  APP_ICON_CHOICES,
  type AppIconChoice,
} from '@electron/shared/app-icon-choice.types';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import {
  useAppIconChoiceQuery,
  useSetAppIconChoiceMutation,
} from '../app-icon-choice.query';
import {
  APP_ICON_CHOICE_DETAILS,
  appIconChoiceAccess,
} from '../app-icon-choices';
import { SettingsSection } from '../settings-section';

export const AppIconPicker = observer(() => {
  const { data: currentIcon } = useAppIconChoiceQuery();
  const setAppIconChoice = useSetAppIconChoiceMutation();
  const { setHash } = useHashRoute();
  const selectedIcon = setAppIconChoice.isPending
    ? setAppIconChoice.variables
    : currentIcon;

  const choose = (icon: AppIconChoice) => {
    if (appIconChoiceAccess(icon) === 'locked') {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source: 'app_icon' });
      setHash('settings/Subscription');
    } else if (icon !== selectedIcon) {
      setAppIconChoice.mutate(icon);
    }
  };

  return (
    <SettingsSection.Item
      direction="vertical"
      searchId="appearance.app-icon"
      className="gap-4"
    >
      <SettingsSection.GroupHeader>App Icon</SettingsSection.GroupHeader>
      <div className="grid w-full grid-cols-9 gap-1 rounded-xl border border-border bg-surface p-2">
        {APP_ICON_CHOICES.map((icon) => {
          const { label, preview } = APP_ICON_CHOICE_DETAILS[icon];
          const access = appIconChoiceAccess(icon);
          const locked = access === 'locked';
          const selected = icon === selectedIcon;
          return (
            <Tooltip
              key={icon}
              label={label}
              description={locked ? 'Included with any paid plan' : undefined}
            >
              <button
                type="button"
                aria-label={label}
                aria-pressed={selected}
                disabled={access === 'pending'}
                onClick={() => choose(icon)}
                className="flex flex-col items-center gap-1.5 rounded-lg p-1.5 pb-2 focus-ring transition-colors hover:bg-surface-hover"
              >
                <span className="relative w-full">
                  <img
                    src={preview}
                    alt=""
                    draggable={false}
                    className={cn(
                      'aspect-square w-full',
                      locked && 'opacity-60'
                    )}
                  />
                  {locked && (
                    <span className="absolute bottom-[6%] right-[6%] flex size-5 items-center justify-center rounded-full border border-border bg-surface text-foreground-muted">
                      <Icons.lock className="size-3" />
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'size-1 rounded-full',
                    selected && 'bg-foreground-muted'
                  )}
                />
              </button>
            </Tooltip>
          );
        })}
      </div>
    </SettingsSection.Item>
  );
});
